import { useState } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import BlueViewMark from './brand/BlueViewMark';
import ContourBackground from './brand/ContourBackground';
import MapGeoidWordmark from './brand/MapGeoidWordmark';

export default function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err?.message || 'Unable to sign in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden px-4"
      style={{
        background:
          'radial-gradient(ellipse at 12% 0%, rgba(29,110,190,0.28), transparent 55%), radial-gradient(ellipse at 88% 10%, rgba(13,148,136,0.18), transparent 50%), linear-gradient(180deg, #071A33 0%, #0C2947 50%, #103A63 100%)',
      }}
    >
      {/* Same contour texture the signed-in portal's own home page draws
          behind itself — a GIS portal's one ornament, carried onto the one
          screen that renders before there is a module, or an accent colour,
          to draw it for. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <ContourBackground colour="#38BDF8" accent="#0D9488" scale={2.4} />
      </div>

      {/* Forced dark, regardless of whatever theme a signed-in session would
          otherwise carry — there is no account yet to have a preference, and
          this page's own ground is always this dark gradient. See
          DashboardViewer's identical `data-theme` scoping for the same
          pattern elsewhere in this app: every gl- surface class and the
          existing text-slate / text-[#0F2942] utilities below repaint for
          free, no component-level dark branch needed. */}
      <div className="relative w-full max-w-sm" data-theme="dark">
        <div className="mb-6 flex justify-center">
          <BlueViewMark height={48} />
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="gl-card p-6"
        >
          <h1 className="text-lg font-semibold text-[#0F2942]">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Access your GIS workspace.</p>

          <label className="mt-5 block text-xs font-semibold text-slate-600">
            Email
            {/* NAMED, AND MARKED AS THE USERNAME.
                Without these a password manager cannot tell where the username
                belongs, so it guesses — and after sign-in it went looking on
                the landing page and filled the application search box with the
                user's email. Saying so here is what stops the hunt; the search
                box also opts out explicitly, but this is the half that fixes
                the cause rather than the symptom. It is also what makes saving
                and filling this form work properly at all. */}
            <div className="relative mt-1.5">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                name="email"
                id="signin-email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="gl-input py-2 pl-9 pr-3 text-sm"
                placeholder="you@example.com"
              />
            </div>
          </label>

          <label className="mt-3 block text-xs font-semibold text-slate-600">
            Password
            <div className="relative mt-1.5">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="signin-password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="gl-input py-2 pl-9 pr-10 text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="gl-btn gl-btn--accent mt-5 w-full px-4 py-2.5 text-sm"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* The chip stays white/light on purpose, independent of this page's
            own dark theming — the MapGeoid mark is drawn for a light ground,
            same reasoning as glass.css's own `bv-keep-light` escape hatch.
            `bg-white` alone would still be repainted dark by the ancestor
            `data-theme="dark"` above, so it is named explicitly here too. */}
        <div className="mt-5 flex flex-col items-center gap-1.5">
          <p className="text-[11px] text-slate-400">powered by</p>
          <div className="bv-keep-light rounded-lg bg-white px-3 py-1.5 shadow-sm">
            <MapGeoidWordmark size={16} />
          </div>
        </div>
      </div>
    </div>
  );
}
